import type { ReactNode } from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { partnerFieldInputs, type PartnerFieldGroup, type PartnerFieldValues } from '@eis/core';

/**
 * The inputs a partner TYPE describes (SDD-014 PS-8): one section per authoring group
 * (`BasicInformation`, `ContactInformation`, …), one input per property, required markers from the
 * type's own `isRequired`. GENERATED from the live type — nothing here knows what a person or an
 * organisation has, which is what makes it right for a type this build has never seen.
 *
 * Labels are the type's own (authored in the Partner domain, in the tenant's language); they are
 * not translated here — a key per field of every type ever authored would be a second copy of
 * the schema, and getting one wrong relabels an input an operator is typing into.
 *
 * Shared by the partners module (create, edit) and the lead form's "Neuer Partner" draft.
 */
export interface PartnerFieldsFormProps {
  readonly groups: readonly PartnerFieldGroup[];
  readonly values: PartnerFieldValues;
  readonly onChange: (key: string, value: string) => void;
  /** Messages keyed by input key — the console's required check or the service's refusal. */
  readonly errors: Readonly<Record<string, string>>;
  readonly disabled?: boolean;
}

const inputType = (type: string): string => {
  switch (type) {
    case 'date':
      return 'date';
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'number':
      return 'number';
    default:
      return 'text';
  }
};

export function PartnerFieldsForm({ groups, values, onChange, errors, disabled }: PartnerFieldsFormProps): ReactNode {
  const { t } = useTranslation('patterns');
  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('partnerFields.noSchema')}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {groups.map((group) => (
        <Stack key={group.name} spacing={1.5} component="section" aria-label={group.label}>
          <Typography variant="subtitle2" component="h3">
            {group.label}
          </Typography>
          {partnerFieldInputs(group).map(({ key, spec, label }) => (
            <TextField
              key={key}
              size="small"
              fullWidth
              type={inputType(spec.type)}
              label={label}
              value={values[key] ?? ''}
              required={spec.isRequired}
              disabled={disabled === true}
              error={(errors[key] ?? '') !== ''}
              helperText={errors[key] ?? undefined}
              onChange={(event) => {
                onChange(key, event.target.value);
              }}
              slotProps={{
                inputLabel: spec.type === 'date' ? { shrink: true } : undefined,
                htmlInput: { 'data-field': key },
              }}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
